import { Mail, Phone, MessageCircle, Clock } from "lucide-react";
import { useSite } from "../../lib/site";
import { ContactForm } from "../../components/site/ContactForm";
import { Img, Socials } from "../../components/site/Common";
import { StyledText } from "../../components/site/StyledText";
import { RichHtml } from "../../components/site/RichHtml";
import { Seo } from "../../components/site/Seo";

export default function Contact() {
  const { site, t, ui, lang } = useSite();
  const g = site.general || {};
  const p = site.profile || {};
  const H = g.hide || {};
  const photo = !H.contact_photo && (g.contact_photo || p.blog_photo || p.photo);
  const waNum = (g.phone || "").replace(/[^0-9]/g, "");
  const eyebrow = !H.contact_eyebrow && t(g.contact_eyebrow);
  const title = (!H.contact_title && t(g.contact_title)) || ui.reachMe;
  const showText = !H.contact_page_text;
  const pill = "inline-flex items-center gap-2 rounded-full border border-site-border px-4 py-2.5 sm:px-5 sm:py-3 text-sm font-medium hover:border-s-primary hover:text-s-primary transition-colors";
  const contactLd = {
    "@context": "https://schema.org", "@type": "ContactPage",
    name: ui.contact, inLanguage: lang,
    mainEntity: {
      "@type": "Person", name: p.name,
      ...(g.email ? { email: g.email } : {}),
      ...(g.phone ? { telephone: g.phone } : {}),
      ...(Object.values(p.socials || {}).filter(Boolean).length ? { sameAs: Object.values(p.socials).filter(Boolean) } : {}),
    },
  };
  return (
    <section data-testid="contact-page" className="pb-24">
      <Seo title={ui.contact} description={ui.reachText} jsonLd={contactLd} />
      <div className={`max-w-6xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-12 grid gap-10 lg:gap-14 items-start ${photo ? "lg:grid-cols-[0.8fr_1.2fr]" : ""}`}>
        {photo && (
          <div className="relative rise lg:sticky lg:top-28 group overflow-hidden" data-testid="contact-photo-wrap">
            <Img src={photo} alt={p.name} data-testid="contact-photo" loading="eager" className={`relative w-full aspect-[2/3] object-cover object-top transition-transform duration-500 ${g.contact_photo_hover !== false ? "group-hover:scale-[1.03] group-hover:-translate-y-1.5" : ""}`} />
          </div>
        )}
        <div className="space-y-7">
          <div className="rise">
            {eyebrow && <StyledText className="block eyebrow mb-5" style={!H.contact_eyebrow ? g.contact_eyebrow_style : undefined} text={eyebrow} testid="contact-eyebrow" />}
            <StyledText as="h1" className="block font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08]" style={!H.contact_title ? g.contact_title_style : undefined} text={title} testid="page-title" />
            {showText && (t(g.contact_text)
              ? <RichHtml html={t(g.contact_text)} className="mt-5 text-lg text-s-muted" testid="contact-text" />
              : <p className="mt-5 text-lg text-s-muted" data-testid="contact-text">{ui.reachText}</p>)}
          </div>
          <div className="flex flex-wrap items-center gap-3" data-testid="contact-methods">
            {g.email && !H.email && <a href={`mailto:${g.email}`} className={pill} data-testid="contact-email"><Mail size={16} className="text-s-primary" />{g.email}</a>}
            {g.phone && !H.phone && <a href={`tel:${g.phone.replace(/\s/g, "")}`} className={pill} data-testid="contact-phone"><Phone size={16} className="text-s-primary" />{g.phone}</a>}
            {waNum && !H.phone && (
              <a href={`https://wa.me/${waNum}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp"
                className="inline-flex items-center gap-2 rounded-full bg-s-primary text-s-onprimary px-5 py-3 text-sm font-semibold hover:opacity-90 transition-opacity">
                <MessageCircle size={16} /> WhatsApp
              </a>
            )}
          </div>
          {!H.form && (
            <div className="rounded-3xl border border-site-border bg-s-surface p-6 sm:p-10">
              <ContactForm type="contact" />
            </div>
          )}
          <div className="flex items-start gap-3 text-sm text-s-muted" data-testid="contact-response-note">
            <Clock size={16} className="mt-0.5 text-s-primary shrink-0" />
            <span>{lang === "tr" ? "Mesajlarınıza genellikle 24 saat içinde dönüş yapıyorum." : "I usually get back to messages within 24 hours."}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
